import { useEffect, useRef, useState, type ComponentType } from 'react';
import { NavLink } from 'react-router-dom';
import {
  Image,
  ImageOff,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  Sparkles,
  Sun,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { CURRENT_VERSION } from '@/lib/version';
import { useFreshUpdate } from '@/hooks/useFreshUpdate';
import { useStorSkarm } from '@/hooks/useStorSkarm';
import { ChangelogModal } from './ChangelogModal';
import { EL_KRETSEN_LOGO_URL } from '@/lib/branding';

export interface SidebarItem {
  to: string;
  label: string;
  /** Lucide-ikon eller egen glyf med samma props (size, strokeWidth, className). */
  icon: ComponentType<{ size?: number | string; strokeWidth?: number | string; className?: string }>;
  badge?: string;
  adminOnly?: boolean;
  /** Syns bara för mottagarna i VITE_PRESENTATION_EMAILS (säljpresentationen). */
  presentationOnly?: boolean;
  group: string;
}

export interface SidebarGroup {
  key: string;
  label: string;
}

interface SidebarProps {
  items: SidebarItem[];
  groups: SidebarGroup[];
  collapsed: boolean;
  onToggle: () => void;
  /** Bakgrundsbilden är ett personligt val — se useBackdrop. */
  backdrop: boolean;
  onToggleBackdrop: () => void;
  /** Mörkt tema, likaså personligt — se useTheme. */
  morkt: boolean;
  onToggleTema: () => void;
  /** Under lg ligger menyn som en utfälld panel över innehållet. */
  mobilOppen: boolean;
  onStangMobil: () => void;
}

/**
 * Spalt i El-kretsens blå, uppbyggd som Hammarby-panelen: grupprubriker i
 * spärrade versaler och en aktiv post som är en halvgenomskinlig vit platta.
 *
 * Ljust tema: ljus babyblå spalt med marin text och cyan-tonad pill. Mörkt
 * tema: djup marin spalt med vit text och vit pill. Färgerna bor i nav-
 * tokens (tokens.css) så att komponenten är densamma i båda.
 */
export function Sidebar({
  items,
  groups,
  collapsed: minimeradValt,
  onToggle,
  backdrop,
  onToggleBackdrop,
  morkt,
  onToggleTema,
  mobilOppen,
  onStangMobil,
}: SidebarProps) {
  const [changelogOpen, setChangelogOpen] = useState(false);
  // Minimeringen finns bara på dator. Ett minimerat läge sparat där får inte
  // följa med till mobilen, där knappen för att ångra det är dold.
  const storSkarm = useStorSkarm();
  const collapsed = minimeradValt && storSkarm;
  // En stängd mobilmeny är bara utskjuten ur bild – utan inert låg dess länkar
  // kvar i tabbordningen och för skärmläsare.
  const dold = !storSkarm && !mobilOppen;
  const navRef = useRef<HTMLElement>(null);

  // Öppnad mobilmeny: fokus till första länken, Escape stänger och lämnar
  // tillbaka fokus till menyknappen.
  useEffect(() => {
    if (!mobilOppen || storSkarm) return;
    navRef.current?.querySelector<HTMLElement>('a')?.focus();
    const pa = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      onStangMobil();
      document.querySelector<HTMLElement>('[aria-controls="primar-meny"]')?.focus();
    };
    window.addEventListener('keydown', pa);
    return () => window.removeEventListener('keydown', pa);
  }, [mobilOppen, storSkarm, onStangMobil]);
  const { isFresh, markSeen } = useFreshUpdate();

  function openChangelog() {
    setChangelogOpen(true);
    markSeen();
  }

  return (
    <>
      {/* Mobil: slöja bakom den utfällda menyn, klick stänger. */}
      {mobilOppen && (
        <div
          className="lg:hidden fixed inset-0 z-30 bg-[rgb(var(--surface-overlay)/var(--surface-overlay-alpha))]"
          onClick={onStangMobil}
          aria-hidden
        />
      )}
      <aside
        id="primar-meny"
        // React 18 saknar typen för inert men skickar attributet vidare som sträng.
        {...({ inert: dold ? '' : undefined } as Record<string, string | undefined>)}
        className={cn(
          'spalt flex flex-col shrink-0 bg-nav text-nav-ink border-r border-nav-strong/10',
          'fixed inset-y-0 left-0 z-40 w-[256px] transition-transform duration-200 ease-out',
          mobilOppen ? 'translate-x-0' : '-translate-x-full',
          'lg:static lg:translate-x-0 lg:transition-[width]',
          collapsed ? 'lg:w-[68px]' : 'lg:w-[256px]',
        )}
      >
      <div
        className={cn(
          'shrink-0 flex items-center gap-2.5 px-[18px] pt-5 pb-6',
          collapsed && 'lg:justify-center lg:px-2',
        )}
      >
        <span className="bg-white rounded-[8px] px-2 py-1.5 shrink-0">
          <img
            src={EL_KRETSEN_LOGO_URL}
            alt="El-kretsen"
            className={cn('w-auto h-[18px]', collapsed && 'lg:h-3')}
          />
        </span>
        <span className={cn('flex items-center gap-2.5', collapsed && 'lg:hidden')}>
          <span className="w-px h-4 bg-nav-strong/25" aria-hidden />
          <span className="text-[13.5px] font-semibold text-nav-strong whitespace-nowrap">ELvis Hub</span>
        </span>
      </div>

      <nav ref={navRef} className="flex-1 overflow-y-auto px-3.5" aria-label="Primär navigation">
        {groups.map((group, groupIndex) => {
          const groupItems = items.filter((i) => i.group === group.key);
          // En grupp utan synliga poster ska inte lämna kvar en tom rubrik.
          if (groupItems.length === 0) return null;

          return (
            <div key={group.key} className={groupIndex > 0 ? 'mt-5' : ''}>
              <div
                className={cn(
                  'px-2.5 mb-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-nav-ink',
                  collapsed && 'lg:hidden',
                )}
              >
                {group.label}
              </div>
              <ul className="space-y-0.5">
                {groupItems.map(({ to, label, icon: Icon, badge }) => (
                  <li key={to}>
                    <NavLink
                      to={to}
                      // Startsidan matchar bara exakt; övriga följer med till undersidor (/moten/:id).
                      end={to === '/'}
                      title={collapsed ? label : undefined}
                      onClick={onStangMobil}
                      className={({ isActive }) =>
                        cn(
                          'group relative flex items-center justify-between gap-2.5 rounded-[9px] px-2.5 py-2',
                          'text-[14.5px] transition-colors duration-150',
                          collapsed && 'lg:justify-center lg:px-0',
                          // Hammarbys pill: halvgenomskinlig vit platta, ingen
                          // kant eller skugga. Fet text bär markeringen även för
                          // den som inte ser plattans ljushetsskillnad.
                          isActive
                            ? 'bg-nav-soft/[0.26] text-nav-strong font-semibold'
                            : 'text-nav-ink font-medium hover:bg-nav-soft/[0.13] hover:text-nav-strong',
                        )
                      }
                    >
                      <>
                          <span className="flex items-center gap-[11px] min-w-0">
                            {/* Ikonen ärver textfärgen: i minimerat läge är den
                                länkens enda synliga identifierare och måste klara 3:1. */}
                            <Icon size={16} strokeWidth={2} className="shrink-0" />
                            <span className={cn('truncate', collapsed && 'lg:hidden')}>{label}</span>
                          </span>
                          {badge && (
                            <span
                              className={cn(
                                'shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold tabular-nums bg-nav-strong text-nav',
                                collapsed && 'lg:hidden',
                              )}
                            >
                              {badge}
                            </span>
                          )}
                      </>
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </nav>

      <div className="px-3.5 py-3 border-t border-nav-strong/15 space-y-0.5 shrink-0">
        <button
          onClick={openChangelog}
          title={collapsed ? `v${CURRENT_VERSION} · visa uppdateringar` : undefined}
          className={cn(
            'relative w-full group inline-flex items-center gap-2.5 px-[11px] py-2 rounded-lg',
            'text-nav-ink hover:text-nav-strong hover:bg-nav-soft/[0.13] transition-colors',
            collapsed && 'justify-center px-0',
          )}
          aria-label={`ELvis Hub version ${CURRENT_VERSION}${
            isFresh ? ' — ny uppdatering tillgänglig' : ''
          } — visa changelog`}
        >
          <span className="relative shrink-0">
            <Sparkles size={14} strokeWidth={2} />
            {isFresh && collapsed && (
              <span
                className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-nav-strong ring-2 ring-nav"
                aria-hidden
              />
            )}
          </span>
          {!collapsed && (
            <>
              <span className="flex-1 text-left text-[12px] font-medium">Uppdateringar</span>
              {isFresh && (
                <span className="w-1.5 h-1.5 rounded-full bg-nav-strong shrink-0" aria-hidden />
              )}
              <span className="text-[11px] font-semibold tabular-nums text-nav-ink">
                v{CURRENT_VERSION}
              </span>
            </>
          )}
        </button>

        <button
          onClick={onToggleTema}
          title={collapsed ? (morkt ? 'Byt till ljust tema' : 'Byt till mörkt tema') : undefined}
          className={cn(
            'w-full inline-flex items-center gap-2.5 px-[11px] py-2 rounded-lg',
            'text-[12px] font-semibold text-nav-ink hover:text-nav-strong hover:bg-nav-soft/[0.13] transition-colors',
            collapsed && 'justify-center px-0',
          )}
          role="switch"
          aria-checked={morkt}
          aria-label="Mörkt tema"
        >
          {morkt ? (
            <Moon size={15} strokeWidth={2} className="shrink-0 text-nav-strong" />
          ) : (
            <Sun size={15} strokeWidth={2} className="shrink-0" />
          )}
          {!collapsed && (
            <>
              <span className="flex-1 text-left">Mörkt tema</span>
              {/* Reglaget säger vilket läge som gäller, inte vad klicket gör */}
              <span
                className={cn(
                  'relative w-7 h-4 rounded-full shrink-0 transition-colors',
                  morkt ? 'bg-nav-strong' : 'bg-nav-strong/30',
                )}
                aria-hidden
              >
                <span
                  className={cn(
                    'absolute top-0.5 w-3 h-3 rounded-full bg-nav transition-all',
                    morkt ? 'left-[14px]' : 'left-0.5',
                  )}
                />
              </span>
            </>
          )}
        </button>

        <button
          onClick={onToggleBackdrop}
          title={collapsed ? (backdrop ? 'Dölj bakgrundsbild' : 'Visa bakgrundsbild') : undefined}
          className={cn(
            'w-full inline-flex items-center gap-2.5 px-[11px] py-2 rounded-lg',
            'text-[12px] font-semibold text-nav-ink hover:text-nav-strong hover:bg-nav-soft/[0.13] transition-colors',
            collapsed && 'justify-center px-0',
          )}
          role="switch"
          aria-checked={backdrop}
          aria-label="Bakgrundsbild"
        >
          {backdrop ? (
            <Image size={15} strokeWidth={2} className="shrink-0 text-nav-strong" />
          ) : (
            <ImageOff size={15} strokeWidth={2} className="shrink-0" />
          )}
          {!collapsed && (
            <>
              <span className="flex-1 text-left">Bakgrundsbild</span>
              {/* Reglaget säger vilket läge som gäller, inte vad klicket gör */}
              <span
                className={cn(
                  'relative w-7 h-4 rounded-full shrink-0 transition-colors',
                  backdrop ? 'bg-nav-strong' : 'bg-nav-strong/30',
                )}
                aria-hidden
              >
                <span
                  className={cn(
                    'absolute top-0.5 w-3 h-3 rounded-full bg-nav transition-all',
                    backdrop ? 'left-[14px]' : 'left-0.5',
                  )}
                />
              </span>
            </>
          )}
        </button>

        <button
          onClick={onToggle}
          className={cn(
            // Minimeringen finns bara på dator — på mobil är menyn utfälld eller borta.
            'w-full hidden lg:inline-flex items-center gap-2.5 px-[11px] py-2 rounded-lg',
            'text-[12px] font-semibold text-nav-ink hover:text-nav-strong hover:bg-nav-soft/[0.13] transition-colors',
            collapsed && 'justify-center px-0',
          )}
          aria-label={collapsed ? 'Expandera sidopanelen' : 'Minimera sidopanelen'}
        >
          {collapsed ? (
            <PanelLeftOpen size={15} strokeWidth={2} />
          ) : (
            <>
              <PanelLeftClose size={15} strokeWidth={2} />
              <span>Minimera</span>
            </>
          )}
        </button>
      </div>

      <ChangelogModal open={changelogOpen} onClose={() => setChangelogOpen(false)} />
      </aside>
    </>
  );
}
